/**
 * The answer options of one question: add, remove, retitle, re-order.
 *
 * ## Why `value` is the first column and not an afterthought
 *
 * An option carries three identifiers and only one of them matters to the engine:
 *
 *   `optionId`    never referenced by any rule — unique and stable, nothing more
 *   `optionText`  what a BD reads
 *   `value`       what every rule compares against: `metric['Q_1_1_1'] === 'a'`
 *
 * So `value` leads, and `optionId` is not editable at all — generated on add. Authors
 * inventing identifiers by hand is the direct cause of two live defects: `Q1.1.7`'s
 * `hasKey` name differs from the real program name only in capitalisation, and on
 * `Q1.1.3.1` the label `e) R-PPA` carries value `f` while `f) 无PPA` carries value `e`.
 * That second one forced the BD frontend to validate one question by label letter and
 * its structurally identical sibling by value — so the two behave differently.
 *
 * ## Deleting is the dangerous operation
 *
 * Removing a value does not break anything loudly. Every rule that matched on it keeps
 * its expression, reads `undefined`, and simply never fires again; the engine raises
 * nothing and the checker cannot see it either. So the count of matching rules is shown
 * on the row, and deleting a referenced value asks first — that is the one moment the
 * information is useful.
 */
import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Input,
  Modal,
  SpaceBetween,
  StatusIndicator,
  Table,
} from '@amzn/awsui-components-react';

import type { AnswerOption, QuestionnaireDefinition, QuestionnaireNode } from '../../types';
import './OptionsEditor.css';
import {
  freshOptionId,
  freshOptionValue,
  labelDisagreesWithValue,
  optionUsage,
  totalUses,
} from '../../utils/options';

export interface OptionsEditorProps {
  definition: QuestionnaireDefinition;
  node: QuestionnaireNode;
  readOnly: boolean;
  onChange: (options: AnswerOption[]) => void;
}

export default function OptionsEditor({
  definition,
  node,
  readOnly,
  onChange,
}: OptionsEditorProps) {
  const options = node.answerOptions ?? [];
  // Keyed on the whole definition: a rule on ANY question can match this one's values,
  // so an edit elsewhere changes these counts.
  const usage = useMemo(() => optionUsage(definition, node.nodeId), [definition, node.nodeId]);
  const [pendingDelete, setPendingDelete] = useState<AnswerOption | null>(null);

  const replace = (next: AnswerOption[]) => onChange(next);
  const edit = (optionId: string, patch: Partial<AnswerOption>) =>
    replace(options.map((o) => (o.optionId === optionId ? { ...o, ...patch } : o)));

  const add = () =>
    replace([
      ...options,
      {
        optionId: freshOptionId(
          node.nodeId,
          options.map((o) => o.optionId),
        ),
        value: freshOptionValue(options.map((o) => o.value)),
        // Blank rather than "New option": a placeholder that looks like content gets
        // shipped. An empty label is visibly unfinished.
        optionText: { id: '', en: '', cn: '' },
      },
    ]);

  const remove = (o: AnswerOption) => {
    replace(options.filter((x) => x.optionId !== o.optionId));
    setPendingDelete(null);
  };

  /** Move an option one place, because order is what a BD sees in the dropdown. */
  const move = (index: number, delta: number) => {
    const to = index + delta;
    if (to < 0 || to >= options.length) return;
    const next = [...options];
    const [m] = next.splice(index, 1);
    next.splice(to, 0, m);
    replace(next);
  };

  const dupValues = new Set(
    options.map((o) => o.value).filter((v, i, all) => all.indexOf(v) !== i),
  );

  return (
    <SpaceBetween size="s">
      {dupValues.size > 0 && (
        <Alert type="error" header="有重复的 value">
          {[...dupValues].map((v) => `「${v}」`).join('、')} 出现了多次。规则按 value
          比较，重复会让其中一个选项永远无法被单独命中。
        </Alert>
      )}

      {options.length === 0 && (
        <Box color="text-status-inactive" padding={{ vertical: 's' }}>
          还没有选项。
        </Box>
      )}

      {options.map((o, i) => {
        const label = o.optionText.cn || o.optionText.en;
        const drift = labelDisagreesWithValue(label, o.value);
        return (
          <div key={o.optionId} className={`cpa-opt ${drift ? 'cpa-opt--drift' : ''}`}>
            <div className="cpa-opt__top">
              <div className="cpa-opt__value">
                <Input
                  value={o.value}
                  disabled={readOnly}
                  invalid={!o.value || dupValues.has(o.value)}
                  onChange={({ detail }) => edit(o.optionId, { value: detail.value })}
                />
              </div>
              {drift && (
                <StatusIndicator type="warning">
                  文案开头是「{/^\s*([A-Za-z0-9])/.exec(label)?.[1]}」
                </StatusIndicator>
              )}
              <div className="cpa-opt__spacer" />
              <div className="cpa-opt__acts">
                <Button
                  variant="inline-icon"
                  iconName="angle-up"
                  ariaLabel="上移"
                  disabled={readOnly || i === 0}
                  onClick={() => move(i, -1)}
                />
                <Button
                  variant="inline-icon"
                  iconName="angle-down"
                  ariaLabel="下移"
                  disabled={readOnly || i === options.length - 1}
                  onClick={() => move(i, 1)}
                />
                <Button
                  variant="inline-icon"
                  iconName="remove"
                  ariaLabel="删除"
                  disabled={readOnly}
                  onClick={() => {
                    // Unreferenced values go without a dialog; the confirmation exists
                    // for the references, so with none there is nothing to confirm.
                    const n = totalUses(
                      usage.get(o.value) ?? { edges: 0, programs: 0, autos: 0, where: [] },
                    );
                    if (n === 0) remove(o);
                    else setPendingDelete(o);
                  }}
                />
              </div>
            </div>

            <div className="cpa-opt__text">
              <span className="cpa-opt__lang">中文</span>
              <div className="cpa-opt__field">
                <Input
                  value={o.optionText.cn ?? ''}
                  disabled={readOnly}
                  placeholder="BD 端读到的文案"
                  invalid={!o.optionText.cn && !o.optionText.en}
                  onChange={({ detail }) =>
                    edit(o.optionId, { optionText: { ...o.optionText, cn: detail.value } })
                  }
                />
              </div>
            </div>
            <div className="cpa-opt__text">
              <span className="cpa-opt__lang">EN</span>
              <div className="cpa-opt__field">
                <Input
                  value={o.optionText.en}
                  disabled={readOnly}
                  onChange={({ detail }) =>
                    edit(o.optionId, { optionText: { ...o.optionText, en: detail.value } })
                  }
                />
              </div>
            </div>
          </div>
        );
      })}

      {!readOnly && (
        <Button iconName="add-plus" onClick={add}>
          添加选项
        </Button>
      )}

      <Modal
        visible={!!pendingDelete}
        onDismiss={() => setPendingDelete(null)}
        header={`删除选项「${pendingDelete ? pendingDelete.optionText.cn || pendingDelete.value : ''}」？`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setPendingDelete(null)}>取消</Button>
              <Button variant="primary" onClick={() => pendingDelete && remove(pendingDelete)}>
                删除
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {pendingDelete && (
          <SpaceBetween size="s">
            <Alert type="warning" header="这些规则按这个 value 比较">
              删掉之后它们的表达式还在，但读到的值再也匹配不上 ——{' '}
              <strong>引擎不会报错，规则只是永远不再命中</strong>。
            </Alert>
            <Table
              variant="embedded"
              items={usage.get(pendingDelete.value)?.where ?? []}
              columnDefinitions={[
                { id: 'n', header: '所在节点', width: 120, cell: (w) => <code>{w.nodeId}</code> },
                {
                  id: 'k',
                  header: '类型',
                  width: 110,
                  cell: (w) =>
                    w.kind === 'edge' ? '条件边' : w.kind === 'program' ? '资格规则' : '自动作答',
                },
                { id: 'i', header: '第几条', cell: (w) => w.index + 1 },
              ]}
            />
          </SpaceBetween>
        )}
      </Modal>
    </SpaceBetween>
  );
}
